import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';

export const EASE = [0.22, 1, 0.36, 1];

export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

// Prvek se při doscrollování jemně vysune a zprůhlední.
export const Reveal = ({ children, as = 'div', delay = 0, y = 40, className, ...rest }) => {
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

// Nadpis, jehož slova postupně vyjíždějí z masky.
export const SplitText = ({ text, as = 'h2', className = '', delay = 0, stagger = 0.06 }) => {
  const Tag = motion[as];
  const words = text.split(' ');
  return (
    <Tag
      key={text}
      className={`split ${className}`}
      aria-label={text}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      variants={{ show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
    >
      {words.map((word, i) => (
        <React.Fragment key={i}>
          <span className="split__mask" aria-hidden="true">
            <motion.span
              className="split__word"
              variants={{
                hidden: { y: '110%' },
                show: { y: '0%', transition: { duration: 0.9, ease: EASE } },
              }}
            >
              {word}
            </motion.span>
          </span>
          {i < words.length - 1 && ' '}
        </React.Fragment>
      ))}
    </Tag>
  );
};

// Obrázek se odkryje zespodu (clip-path) a při scrollu se jemně posouvá (parallax).
export const ImageReveal = ({ src, alt, className = '', parallax = 8, loading = 'lazy' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${parallax}%`, `${parallax}%`]);

  // whileInView musí být na obalu bez clip-path – Chrome jinak prvek oříznutý
  // na nulu nepovažuje za viditelný a animace by se nespustila.
  return (
    <motion.div
      ref={ref}
      className={`img-reveal ${className}`}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
    >
      <motion.div
        className="img-reveal__clip"
        variants={{
          hidden: { clipPath: 'inset(100% 0% 0% 0%)' },
          show: { clipPath: 'inset(0% 0% 0% 0%)', transition: { duration: 1.3, ease: EASE } },
        }}
      >
        <motion.div
          className="img-reveal__inner"
          variants={{
            hidden: { scale: 1.3 },
            show: { scale: 1, transition: { duration: 1.6, ease: EASE } },
          }}
        >
          <motion.img
            src={src}
            alt={alt}
            loading={loading}
            style={{ y, scale: 1 + (parallax * 2) / 100 }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Tlačítko, které se „přitahuje“ ke kurzoru.
export const Magnetic = ({ children, strength = 0.35, className = '' }) => {
  const ref = useRef(null);
  const spring = { stiffness: 220, damping: 16, mass: 0.4 };
  const x = useSpring(0, spring);
  const y = useSpring(0, spring);

  const onMove = (e) => {
    if (!ref.current || e.pointerType !== 'mouse') return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      className={`magnetic ${className}`}
      style={{ x, y, display: 'inline-block' }}
      onPointerMove={onMove}
      onPointerLeave={reset}
    >
      {children}
    </motion.div>
  );
};

export const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
